import { RefObject, useEffect } from 'react';

export const ICON_SIZE = 38;
const BAR_WIDTH = 2;
const BAR_SPACING = 3.6;
const CENTER_GAP = 1.6;
const BAR_RADIUS = 1;
const REST_BAR_HEIGHT = 4;
const BARS_PER_SIDE = 3;
const MAX_BAR_HEIGHT = 26;
const WAVE_PERIOD_MS = 700;
const WAVE_PHASE_STEP = 2.1;
const WAVE_BASE = 0.55;
const WAVE_DEPTH = 0.45;
const MAX_FREQUENCY_VALUE = 255;
const SIGNAL_GAIN = 1.2;
const SPEECH_MAX_FREQUENCY_HZ = 4000;
const BAR_COLOR = 'white';

const drawBar = (context: CanvasRenderingContext2D, xPosition: number, barHeight: number): void => {
   context.roundRect(xPosition, (ICON_SIZE - barHeight) / 2, BAR_WIDTH, barHeight, BAR_RADIUS);
};

export const useSoundbarCanvas = (canvasRef: RefObject<HTMLCanvasElement>, analyser: AnalyserNode | null): void => {
   useEffect(() => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (!canvas || !context || analyser === null) {
         return undefined;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.round(ICON_SIZE * devicePixelRatio);
      canvas.height = Math.round(ICON_SIZE * devicePixelRatio);
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const frequencyData = new Uint8Array(analyser.frequencyBinCount);
      const nyquistFrequency = analyser.context.sampleRate / 2;
      const speechBins = Math.min(
         frequencyData.length,
         Math.ceil((SPEECH_MAX_FREQUENCY_HZ / nyquistFrequency) * frequencyData.length)
      );
      const centerX = ICON_SIZE / 2;
      let animationFrameId = 0;

      const readLevel = (): number => {
         analyser.getByteFrequencyData(frequencyData);
         let sum = 0;
         for (let index = 0; index < speechBins; index += 1) {
            sum += frequencyData[index];
         }
         return Math.min(1, (sum / speechBins / MAX_FREQUENCY_VALUE) * SIGNAL_GAIN);
      };

      const renderFrame = (timestamp: number): void => {
         const level = readLevel();
         context.clearRect(0, 0, ICON_SIZE, ICON_SIZE);
         context.fillStyle = BAR_COLOR;
         context.beginPath();

         for (let index = 0; index < BARS_PER_SIDE; index += 1) {
            const phase = (timestamp / WAVE_PERIOD_MS) * Math.PI * 2 + index * WAVE_PHASE_STEP;
            const modulation = WAVE_BASE + WAVE_DEPTH * Math.sin(phase);
            const barHeight = REST_BAR_HEIGHT + level * modulation * (MAX_BAR_HEIGHT - REST_BAR_HEIGHT);
            drawBar(context, centerX + CENTER_GAP / 2 + index * BAR_SPACING, barHeight);
            drawBar(context, centerX - CENTER_GAP / 2 - BAR_WIDTH - index * BAR_SPACING, barHeight);
         }

         context.fill();
         animationFrameId = requestAnimationFrame(renderFrame);
      };

      animationFrameId = requestAnimationFrame(renderFrame);

      return () => {
         cancelAnimationFrame(animationFrameId);
      };
   }, [canvasRef, analyser]);
};
